import { Link } from 'react-router-dom'
import { FiAlertTriangle, FiCheckCircle, FiXCircle, FiShield } from 'react-icons/fi'
import BlogLayout from '../../components/BlogLayout'

const toc = [
  { id: 'es-legal', label: '¿Es legal usar geolocalización para fichar?' },
  { id: 'marco-rgpd', label: 'Marco RGPD: principios aplicables' },
  { id: 'doctrina-aepd', label: 'Doctrina y resoluciones de la AEPD' },
  { id: 'requisitos', label: 'Requisitos para una geolocalización lícita' },
  { id: 'errores', label: 'Errores que sancionan a las empresas' },
  { id: 'configuracion', label: 'Configuración recomendada' },
  { id: 'faq', label: 'Preguntas frecuentes' },
]

const faqs = [
  { question: '¿Puedo geolocalizar a un empleado en su tiempo libre?', answer: 'No. La geolocalización solo es lícita durante la jornada laboral y para una finalidad concreta (fichaje, control de ruta para reparto, etc.). Fuera de jornada, está prohibida y constituye infracción muy grave RGPD.' },
  { question: '¿Tengo que pedir consentimiento expreso al trabajador?', answer: 'No. El consentimiento en relación laboral no se considera libre. La base legal es el "interés legítimo" del empresario y la "ejecución del contrato" (art. 6 RGPD), siempre superando el test de proporcionalidad. Sí debes informar previamente, no pedir permiso.' },
  { question: '¿Qué información debo dar al trabajador?', answer: 'Antes de activar la geolocalización: finalidad, base legal, datos recogidos, conservación, derechos ARCO, contacto del DPO y el resultado del análisis de proporcionalidad. Lo habitual es entregar una nota informativa firmada.' },
  { question: '¿Vale con guardar solo el centro de trabajo?', answer: 'Sí, y es lo más recomendable. Si lo único que necesitas es validar que el fichaje se hace dentro de un radio razonable del centro, guarda solo "dentro/fuera" en lugar de coordenadas exactas. Reduce drásticamente el riesgo RGPD.' },
  { question: '¿Cuánto tiempo puedo conservar las coordenadas?', answer: 'El plazo debe ser el mínimo necesario para la finalidad. La AEPD considera razonable conservar entre 30 y 90 días los datos de localización. Si los necesitas para defensa frente a reclamaciones, archívalos pseudonimizados.' },
]

export default function GeolocalizacionFichajes() {
  return (
    <BlogLayout slug="geolocalizacion-fichajes-rgpd" toc={toc} faqs={faqs}>
      <section id="es-legal" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">¿Es legal usar geolocalización para fichar?</h2>
        <p>
          Respuesta corta: <strong>sí, dentro de unos límites estrictos</strong>. La empresa puede usar la posición GPS o la red para validar que un fichaje se hace desde un lugar coherente, pero no puede convertirlo en un seguimiento permanente del trabajador.
        </p>
        <p>
          La obligación de registro horario (art. 34.9 ET) y la finalidad legítima de control empresarial (art. 20.3 ET) son la base que sostiene la práctica. El RGPD añade requisitos de proporcionalidad e información que muchas empresas pasan por alto y acaban en sanción. Si todavía estás <Link to="/blog/como-elegir-sistema-fichaje">comparando soluciones de fichaje</Link>, la geolocalización debería ser uno de los criterios técnicos prioritarios.
        </p>
      </section>

      <section id="marco-rgpd" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Marco RGPD: principios aplicables</h2>
        <p>El art. 5 RGPD obliga a tratar los datos con varios principios:</p>
        <ul className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Limitación de la finalidad.</strong> Solo para validar la ubicación del fichaje. No para vigilancia continua, productividad ni rendimiento.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Minimización de datos.</strong> Recoge lo justo: posición en el momento del fichaje, no histórico completo del día.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Limitación del plazo.</strong> Conservación reducida y borrado automático.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Transparencia.</strong> El trabajador debe saber qué se recoge, cuándo y para qué.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div><strong>Proporcionalidad.</strong> Test de los tres pasos: idónea, necesaria y proporcionada en sentido estricto.</div>
          </li>
        </ul>
      </section>

      <section id="doctrina-aepd" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Doctrina y resoluciones de la AEPD</h2>
        <p>
          La Agencia Española de Protección de Datos ha resuelto numerosos expedientes que perfilan dónde está la línea:
        </p>
        <div className="space-y-4 my-6">
          <div className="border-l-4 border-blue-400 pl-4 py-2">
            <p className="font-semibold text-gray-900 mb-1">PS/00120/2022 — Multa 30.000 €</p>
            <p className="text-sm text-gray-600">Empresa de transporte que activaba GPS continuo en el móvil personal del trabajador. Falta de información, exceso de finalidad y conservación indefinida.</p>
          </div>
          <div className="border-l-4 border-blue-400 pl-4 py-2">
            <p className="font-semibold text-gray-900 mb-1">PS/00347/2023 — Apercibimiento</p>
            <p className="text-sm text-gray-600">Empresa de servicios con app de fichaje. La AEPD validó la geolocalización en el momento del fichaje, pero apercibió por no informar previamente y no firmar acuerdo con el comité.</p>
          </div>
          <div className="border-l-4 border-blue-400 pl-4 py-2">
            <p className="font-semibold text-gray-900 mb-1">PS/00098/2024 — Archivo</p>
            <p className="text-sm text-gray-600">Empresa que solo registraba "dentro/fuera del centro" como booleano y borraba la coordenada exacta a los 30 días. Modelo de buena práctica.</p>
          </div>
        </div>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg my-6">
          <p className="text-blue-900 text-sm"><strong>La línea AEPD en pocas palabras:</strong> el dato puntual de fichaje sí, el seguimiento continuo no.</p>
        </div>
      </section>

      <section id="requisitos" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Requisitos para una geolocalización lícita</h2>
        <p>Para que la práctica supere una inspección, debes cumplir simultáneamente:</p>
        <ol className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
            <div><strong>Análisis de impacto (DPIA).</strong> Documento interno justificando proporcionalidad y necesidad. Obligatorio si tratas datos a gran escala o de forma sistemática.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
            <div><strong>Información previa al trabajador</strong> (art. 13 RGPD + art. 90 LOPDGDD). Por escrito, con acuse de recibo.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
            <div><strong>Consulta a la representación legal de los trabajadores</strong> si existe (art. 64.5.f ET).</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
            <div><strong>Política de privacidad y registro de actividad</strong> actualizados.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
            <div><strong>Encargado de tratamiento</strong> firmado con el proveedor del software.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">6</span>
            <div><strong>Datos en servidores UE.</strong> Si están fuera, garantías reforzadas y cláusulas contractuales tipo.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">7</span>
            <div><strong>Acceso del trabajador a sus datos</strong> (derecho ARCO/portabilidad operativo en menos de 30 días).</div>
          </li>
        </ol>
      </section>

      <section id="errores" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Errores que sancionan a las empresas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-5">
            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2"><FiXCircle className="h-5 w-5" /> Tracking continuo</h3>
            <p className="text-sm text-red-800">Activar GPS toda la jornada en lugar de solo en el momento del fichaje. Es la causa más frecuente de sanción.</p>
          </div>
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-5">
            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2"><FiXCircle className="h-5 w-5" /> Móvil personal sin política BYOD</h3>
            <p className="text-sm text-red-800">Obligar a usar el teléfono personal sin compensación ni acuerdo expreso. Vulnera el derecho a la intimidad.</p>
          </div>
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-5">
            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2"><FiXCircle className="h-5 w-5" /> Información implícita</h3>
            <p className="text-sm text-red-800">"Lo pone en el contrato" no basta. Necesitas información expresa y por escrito sobre el tratamiento concreto.</p>
          </div>
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-5">
            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2"><FiXCircle className="h-5 w-5" /> Conservación indefinida</h3>
            <p className="text-sm text-red-800">Guardar coordenadas durante años sin justificar el plazo. La AEPD considera 90 días el límite razonable.</p>
          </div>
        </div>
      </section>

      <section id="configuracion" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Configuración recomendada</h2>
        <p>
          Si tu empresa quiere implantar geolocalización con bajo riesgo legal, este es el patrón más defendible:
        </p>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span><strong>Activación solo en el clic de fichaje</strong> (entrada/salida/pausa). Apagado el resto del tiempo.</span></li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span><strong>Geocerca por centro de trabajo</strong> con radio configurable (50-200 m).</span></li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span><strong>Almacenamiento mínimo:</strong> dentro/fuera + precisión, no la coordenada exacta más allá de 30 días.</span></li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span><strong>Notificación al trabajador</strong> en cada uso ("Estás compartiendo tu ubicación para validar tu fichaje").</span></li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span><strong>Acceso del empleado</strong> a su histórico de fichajes con coordenadas asociadas.</span></li>
        </ul>
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg my-6">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800 mb-1">Atención teletrabajo</p>
              <p className="text-amber-700 text-sm">Si trabajan desde casa, no actives geolocalización: no aporta nada legítimo y entras en el domicilio del trabajador, terreno protegido por art. 18 CE. Para este escenario, consulta <Link to="/blog/fichaje-teletrabajo-remoto">los métodos específicos de fichaje en teletrabajo</Link>.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div key={f.question}>
              <h3 className="font-semibold text-gray-900 mb-1">{f.question}</h3>
              <p className="text-gray-600 text-sm">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-gray-100 pt-8 mt-12 text-sm text-gray-500">
        Si quieres ver cómo PresenzIA implementa la geolocalización con todos estos requisitos, consulta
        <Link to="/fichaje-digital" className="text-blue-600 hover:underline mx-1">la página de fichaje digital</Link>
        y revisa nuestra
        <Link to="/privacidad" className="text-blue-600 hover:underline mx-1">política de privacidad</Link>.
      </div>
    </BlogLayout>
  )
}
